import React, { useEffect, useState } from 'react';
import { Box, Tabs, Tab, Typography, Accordion, AccordionDetails, Skeleton, useTheme } from '@mui/material';
import moment from 'moment';
import { useLocation, useSearchParams } from 'react-router-dom';
import DeviceList, { Device } from './DeviceList';
import TrendsCard from './TrendsCard';
import EventsTrendsCard from './EventsTrendsCard';
import HistoryTable from './HistoryTable';
import AssetHistoryTable from "./AssetHistoryTable";
import axiosInstance from '../../../axios/axios';
import { END_POINTS } from '../../../shared';
import noDataReport from "../../../assets/no_data_report.svg";
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';

type AssetDetails = {
    id?: string;
    device_name?: string | null;
    vendor?: string | null;
    model?: string | null;
    device_type?: string | null;
    device_version?: string | null;
    criticality?: string | null;
    last_scan_date?: string | null;
    total_scans?: number | null;
    region_name?: string | null;
};

type AssetHistoryItem = {
    id: string;
    timestamp?: string;
    no_of_changes?: number;
    score_changes?: number;
    misconfiguration_changes?: number;
    cve_changes?: number;
    status?: string;
    change_breakdown?: {
        all?: number;
        additions?: number;
        deletions?: number;
        modified?: number;
    };
};

const configDiff = () => {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";

    // Get device_id and activeTab from location state (set by ShowConfigDiffPage back navigation)
    const locationState = location.state as { device_id?: string; activeTab?: number; eventDeviceId?: string } | null;
    const initialDeviceId = locationState?.device_id;
    // eventDeviceId: passed back from Events-tab show-diff so EventsTrendsCard gets a deviceId
    // Uses a different key from device_id to avoid triggering single-asset mode
    const eventDeviceId = locationState?.eventDeviceId;

    // In sidebar mode, the selected device is also persisted in the URL param `deviceId`
    // so navigate(-1) can restore it after remounting.
    const deviceIdFromUrl = !initialDeviceId ? (searchParams.get('deviceId') ?? '') : '';

    // Tab priority: location state (from back navigation) > URL param > default 0
    const tabFromState = locationState?.activeTab;
    const tabFromUrl = searchParams.get('tab');
    const initialTab = tabFromState ?? (tabFromUrl !== null ? Number(tabFromUrl) : 0);

    const [selectedDevice, setSelectedDevice] = useState<Device | null>(
        initialDeviceId
            ? { id: initialDeviceId, type: '' }
            : deviceIdFromUrl
                ? { id: deviceIdFromUrl, type: '' }
                : eventDeviceId
                    ? { id: eventDeviceId, type: '' }
                    : null
    );
    const [assetDetails, setAssetDetails] = useState<AssetDetails | null>(null);
    const [detailsLoading, setDetailsLoading] = useState(!!initialDeviceId);
    const [assetHistoryData, setAssetHistoryData] = useState<AssetHistoryItem[] | null>(null);
    const [assetHistoryPage, setAssetHistoryPage] = useState<number>(1);
    const [assetHistoryPageSize, setAssetHistoryPageSize] = useState<number>(10);
    const [assetHistoryLoading, setAssetHistoryLoading] = useState<boolean>(false);
    const isNewUserTrue = useSelector(
        (state: RootState) => state.UserLoginDetails.isNewUserTrue
    );

    const [value, setValue] = useState(initialTab);

    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        // Update URL param so tab persists on refresh (like MyScanTable's 'view' param)
        if (newValue === 1) {
            setSearchParams({ tab: '1' });
        } else {
            setSearchParams({});
        }
    };

    // Sync tab value when location state changes (handles navigation back from ShowConfigDiffPage)
    useEffect(() => {
        if (locationState?.activeTab !== undefined) {
            setValue(locationState.activeTab);
            // Also update URL param to match the restored tab
            if (locationState.activeTab === 1) {
                setSearchParams({ tab: '1' });
            } else {
                setSearchParams({});
            }
        }
    }, [locationState?.activeTab]);

    const [detailsOpen, setDetailsOpen] = useState(true);

    // Sync selectedDevice with initialDeviceId from location state (handles dropdown in header)
    useEffect(() => {
        if (initialDeviceId) {
            setSelectedDevice((prev) => {
                if (prev?.id === initialDeviceId) return prev;
                return { id: initialDeviceId, type: "" };
            });
        }
    }, [initialDeviceId]);


    useEffect(() => {
        const deviceId = selectedDevice?.id;
        if (!deviceId) {
            setAssetDetails(null);
            setDetailsLoading(false);
            return;
        }

        let ignore = false;

        // Set loading FIRST to prevent "no data" flash
        setDetailsLoading(true);
        setAssetDetails(null);

        const fetchAssetDetails = async () => {
            try {
                const resp = await axiosInstance.get(END_POINTS.ASSET_CONFIG_DIFF, {
                    params: { asset_id: deviceId },
                });
                if (ignore) {
                    return;
                }
                const data = resp?.data?.data;
                if (data) {
                    setAssetDetails({
                        id: data.id ?? deviceId,
                        device_name: data.device_name ?? selectedDevice.device_name,
                        vendor: data.vendor ?? selectedDevice.vendor,
                        model: data.model ?? selectedDevice.model,
                        device_type: data.device_type ?? selectedDevice.type,
                        device_version: data.device_version ?? (selectedDevice as any)?.version ?? null,
                        criticality: data.criticality ?? selectedDevice.criticality ?? null,
                        last_scan_date: data.last_scan_date ?? selectedDevice.last_scan ?? null,
                        total_scans: typeof data.total_scans === 'number' ? data.total_scans : selectedDevice.total_scans ?? null,
                        region_name: data.region_name ?? selectedDevice.region_name ?? null,
                    });
                } else {
                    setAssetDetails(null);
                }
            } catch (error) {
                console.error('Error fetching asset details:', error);
                if (!ignore) {
                    setAssetDetails(null);
                }
            } finally {
                if (!ignore) {
                    setDetailsLoading(false);
                }
            }
        };

        fetchAssetDetails();
        return () => {
            ignore = true;
        };
    }, [selectedDevice]);

    const fallbackDash = '-';
    const resolvedDetails = {
        name: assetDetails?.device_name ?? selectedDevice?.device_name ?? selectedDevice?.id ?? fallbackDash,
        vendor: assetDetails?.vendor ?? selectedDevice?.vendor ?? fallbackDash,
        model: assetDetails?.model ?? selectedDevice?.model ?? null,
        type: assetDetails?.device_type ?? selectedDevice?.type ?? fallbackDash,
        version: assetDetails?.device_version ?? (selectedDevice as any)?.version ?? fallbackDash,
        region: assetDetails?.region_name ?? selectedDevice?.region_name ?? fallbackDash,
        criticality: assetDetails?.criticality ?? selectedDevice?.criticality ?? fallbackDash,
        lastScan: assetDetails?.last_scan_date ?? selectedDevice?.last_scan ?? null,
        totalScans: (assetDetails?.total_scans ?? selectedDevice?.total_scans) ?? fallbackDash,
    };

    useEffect(() => {
        const deviceId = selectedDevice?.id;
        if (!deviceId) {
            setAssetHistoryData(null);
            return;
        }

        let ignore = false;
        const fetchAssetHistory = async () => {
            try {
                setAssetHistoryLoading(true);
                const resp = await axiosInstance.get(
                    `${END_POINTS.ASSET_CONFIG_DIFF_HISTORY}/${deviceId}/history`,
                    {
                        params: {
                            page: assetHistoryPage,
                            page_size: assetHistoryPageSize,
                        },
                    }
                );
                if (ignore) {
                    return;
                }
                const data = resp?.data?.data || resp?.data;
                // Handle different response formats: array directly, or { results: [] }
                const results = Array.isArray(data?.results)
                    ? data.results
                    : Array.isArray(data)
                        ? data
                        : [];
                //setAssetHistoryData(results);
                const scanDates = await fetchScanDeviceDates(deviceId);
                const mergedResults = results.map((item: AssetHistoryItem) => {
                    const matchedScan = scanDates?.find((scan: any) => scan.id === item.id);
                    return {
                        ...item,
                        file_id: matchedScan?.file_id,
                        date_added: matchedScan?.date_added,
                        security_score: matchedScan?.security_score,
                    };
                });
                const finalResults = mergedResults.map((item: AssetHistoryItem, index: number) => {
                    // Previous history is the next item in the list (chronologically older)
                    const previousItem = mergedResults[index + 1];
                    return {
                        ...item,
                        previous_file: previousItem || null
                    };
                });

                setAssetHistoryData(finalResults);


            } catch (error) {
                if (!ignore) {
                    console.error('Error fetching asset config diff history:', error);
                    setAssetHistoryData([]);
                }
            } finally {
                if (!ignore) {
                    setAssetHistoryLoading(false);
                }
            }
        };

        fetchAssetHistory();
        return () => {
            ignore = true;
        };
    }, [selectedDevice?.id, assetHistoryPage, assetHistoryPageSize]);

    const fetchScanDeviceDates = async (device_id: string) => {
        try {
            //setLoading(true);
            const end_date = format(new Date(), "yyyy-MM-dd");
            const start_date = format(
                new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
                "yyyy-MM-dd"
            );

            const response = await axiosInstance.post(
                END_POINTS.DEVICE_CYBER_RESILIENCE_SCAN_DATES,
                {
                    device_id,
                    duration: "custom_date_range",
                    start_date,
                    end_date,
                }
            );

            if (response?.status === 200 && response?.data?.data?.length > 0) {
                return response.data.data;
            } else {
                return [];
                // setLoading(false);
            }
        } catch (error) {
            console.error("Error fetching scanned device dates:", error);
            //setLoading(false);
        }
    };


    // Reset pagination when device changes
    useEffect(() => {
        setAssetHistoryPage(1);
    }, [selectedDevice?.id]);


    const formattedLastScan = resolvedDetails.lastScan
        ? moment(resolvedDetails.lastScan).format('D MMM YYYY')
        : fallbackDash;

    // Flag to check if we're navigating back from ShowConfigDiffPage (with device_id in state)
    // In this case, we should not show "no data" or disable Events tab while data is loading
    const navigatedFromShowConfigDiff = !!locationState?.device_id;

    // In sidebar mode: persist the selected device in the URL param `deviceId` (replace current
    // history entry so it doesn't add extra entries). This means navigate(-1) from show-diff
    // will restore the URL including the deviceId param, so ConfigDiff can re-init the device.
    useEffect(() => {
        if (navigatedFromShowConfigDiff) return; // single-asset mode — don't touch URL
        if (selectedDevice?.id) {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('deviceId', selectedDevice.id);
                    return next;
                },
                { replace: true }
            );
        } else {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('deviceId');
                    return next;
                },
                { replace: true }
            );
        }
    }, [selectedDevice?.id, navigatedFromShowConfigDiff]);

    // Flag when there is no asset config data returned by the API or user is new
    // IMPORTANT: Don't show "no data" when navigating from ShowConfigDiff - show loading instead
    const isNoDataAvailable = !navigatedFromShowConfigDiff && (
        isNewUserTrue ||
        (!!selectedDevice && !detailsLoading && !assetDetails)
    );

    // Prevent landing on Events tab when data is unavailable
    // BUT: Don't force back to Assets if we're coming from navigation state with activeTab=1
    // (in that case, we trust the navigation and let API calls populate the data)
    useEffect(() => {
        // Only force back to Assets if data is unavailable AND we didn't explicitly navigate to Events tab
        const navigatedToEventsTab = locationState?.activeTab === 1;
        if (isNoDataAvailable && value === 1 && !navigatedToEventsTab) {
            setValue(0);
        }
    }, [isNoDataAvailable, value, locationState?.activeTab]);

    return (
        <>
            {!navigatedFromShowConfigDiff && (
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        mb: 2,
                        boxShadow: 'none',
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Tabs
                        value={value}
                        onChange={handleChange}
                        aria-label="config diff tabs"
                        TabIndicatorProps={{
                            sx: {
                                backgroundColor: "#3A2ADA",
                                height: "3px",
                                borderRadius: "3px 3px 0 0"
                            }
                        }}
                    >
                        <Tab
                            label={
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: "bold",
                                        color: value === 0 ? "#3A2ADA" : "#8787AE",
                                    }}
                                >
                                    Assets
                                </Typography>
                            }
                            sx={{
                                minWidth: 100,
                                "&.Mui-selected": { color: "#3A2ADA" },
                                "&:not(.Mui-selected)": { color: "#8787AE" },
                                textTransform: "none",
                            }}
                        />
                        <Tab
                            label={
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: "bold",
                                        color: value === 1 ? "#3A2ADA" : "#8787AE",
                                    }}
                                >
                                    Events
                                </Typography>
                            }
                            sx={{
                                minWidth: 100,
                                "&.Mui-selected": { color: "#3A2ADA" },
                                "&:not(.Mui-selected)": { color: "#8787AE" },
                                textTransform: "none",
                            }}
                            disabled={isNoDataAvailable && !navigatedFromShowConfigDiff}
                        />
                    </Tabs>
                </Box>
            )}
            {/* Tab Content */}
            <Box sx={{ width: '100%' }}>
                {value === 0 && (
                    isNoDataAvailable ? (
                        <Box
                            sx={{
                                width: '100%',
                                minHeight: '600px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    gap: 1.5,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={noDataReport}
                                    alt="No assets available"
                                    sx={{ width: 220, opacity: 0.9 }}
                                />
                                <Typography
                                    sx={{
                                        color: isDarkMode ? "#cccccc" : "#222222",
                                        fontWeight: 700,
                                        fontSize: 20,
                                    }}
                                >
                                    There is no asset available,
                                </Typography>
                                <Typography
                                    sx={{
                                        color: isDarkMode ? "#aaaaaa" : "#555555",
                                        fontWeight: 500,
                                        fontSize: 16,
                                    }}
                                >
                                    To see the configuration, add asset into the system.
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{
                            display: 'flex',
                            gap: 2,
                            alignItems: 'flex-start',
                            width: '100%',
                            minHeight: '600px',
                        }}>
                            {/* Left: asset list */}
                            {!navigatedFromShowConfigDiff && (
                                <DeviceList
                                    onSelect={(d) => {
                                        // Set loading immediately with device selection to prevent flash
                                        if (d?.id) {
                                            setDetailsLoading(true);
                                        }
                                        setSelectedDevice(d);
                                    }}
                                    initialSelectedDeviceId={initialDeviceId || deviceIdFromUrl}
                                />
                            )}

                            {/* Right: single big rectangle with heading 'Asset Details' and a single-row of fields */}
                            <Box sx={{
                                flex: 1,
                                minWidth: 0,
                                width: '100%',
                            }}>
                                <Accordion
                                    expanded={detailsOpen}
                                    onChange={(_e, expanded) => setDetailsOpen(expanded)}
                                    sx={{
                                        p: 0,
                                        border: '1px solid rgba(0,0,0,0.08)',
                                        borderRadius: 2,
                                        bgcolor: 'background.paper',
                                        width: '100%',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        boxShadow: 'none',
                                        '&:before': { display: 'none' },
                                    }}
                                    disableGutters
                                >
                                    {/* <AccordionSummary
                                        expandIcon={
                                            <ExpandMoreIcon
                                                sx={{
                                                    color: 'text.primary',
                                                    transition: 'transform 0.3s',
                                                    transform: detailsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                }}
                                            />
                                        }
                                        sx={{
                                            flexDirection: 'row-reverse',
                                            borderBottom: detailsOpen ? `1px solid rgba(0,0,0,0.08)` : 'none',
                                            minHeight: '48px',
                                            '& .MuiAccordionSummary-content': {
                                                margin: '12px 0',
                                                alignItems: 'center',
                                                width: '100%',
                                            },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0 }}>Asset Details</Typography>
                                        </Box>
                                    </AccordionSummary> */}
                                    <AccordionDetails>
                                        {!selectedDevice ? (
                                            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                                Select an asset from the list to view details.
                                            </Typography>
                                        ) : detailsLoading ? (
                                            <Box sx={{
                                                display: 'flex',
                                                gap: 2,
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                width: '100%',
                                            }}>
                                                {Array.from({ length: 8 }).map((_, index) => (
                                                    <Box key={index} sx={{
                                                        minWidth: 120,
                                                        flex: '1 1 auto',
                                                        maxWidth: '200px',
                                                    }}>
                                                        <Skeleton variant="text" width="60%" height={16} sx={{ mb: 0.5 }} />
                                                        <Skeleton variant="text" width="80%" height={20} />
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Box sx={{
                                                display: 'flex',
                                                gap: 2,
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                width: '100%',
                                            }}>
                                                {[
                                                    { label: 'Name', value: resolvedDetails.name || "-" },
                                                    { label: 'Vendor', value: resolvedDetails.vendor || "-" },
                                                    { label: 'OS, Type', value: resolvedDetails.model ? `${resolvedDetails.model} / ${resolvedDetails.type}` : "-" },
                                                    { label: 'Version', value: resolvedDetails.version || "-" },
                                                    { label: 'Region', value: resolvedDetails.region },
                                                    { label: 'Criticality', value: resolvedDetails.criticality || "-" },
                                                    { label: 'Last Scan Date', value: formattedLastScan || "-" },
                                                    { label: 'Total Scans', value: resolvedDetails.totalScans ?? '—' },
                                                ].map((item) => {
                                                    const needsEllipsis = item.label === 'Model, Type' || item.label === 'Region';
                                                    return (
                                                        <Box key={item.label} sx={{
                                                            minWidth: 120,
                                                            flex: '1 1 auto',
                                                            maxWidth: '200px',
                                                            ...(needsEllipsis && { overflow: 'hidden' }),
                                                        }}>
                                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>{item.label}</Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                    fontWeight: 700,
                                                                    ...(needsEllipsis && {
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                    }),
                                                                }}
                                                                title={needsEllipsis ? item.value ?? fallbackDash : undefined}
                                                            >
                                                                {item.value ?? fallbackDash}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        )}
                                    </AccordionDetails>
                                </Accordion>

                                {/* Add Trends and History below the asset details so they are visible in the Assets tab */}
                                {selectedDevice && (
                                    <Box sx={{
                                        mt: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        width: '100%',
                                    }}>
                                        {/* Compact variants to match Figma - smaller heights */}
                                        <Box>
                                            <TrendsCard compact height={220} deviceId={selectedDevice?.id} />
                                        </Box>
                                        <Box>
                                            <AssetHistoryTable
                                                height={320}
                                                device_id={selectedDevice?.id}
                                                historyData={assetHistoryData}
                                                page={assetHistoryPage}
                                                pageSize={assetHistoryPageSize}
                                                isLoading={assetHistoryLoading}
                                                vendor={assetDetails?.vendor ?? selectedDevice?.vendor ?? assetDetails?.model ?? selectedDevice?.model ?? assetDetails?.device_type ?? selectedDevice?.type}
                                                source={navigatedFromShowConfigDiff ? "assets-page" : "configdiff-sidebar"}
                                                onPageChange={(page, pageSize) => {
                                                    setAssetHistoryPage(page);
                                                    setAssetHistoryPageSize(pageSize);
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )
                )}
                {value === 1 && (
                    <Box sx={{ width: '100%', minHeight: '600px' }}>
                        <EventsTrendsCard height={220} deviceId={selectedDevice?.id} />
                        <Box sx={{ mt: 2 }}>
                            <HistoryTable
                                device_id={selectedDevice?.id}
                                vendor={assetDetails?.vendor ?? selectedDevice?.vendor ?? assetDetails?.model ?? selectedDevice?.model ?? assetDetails?.device_type ?? selectedDevice?.type}
                            />
                        </Box>
                    </Box>
                )}
            </Box>
        </>
    );
};

export default configDiff;